import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, BookOpen, CalendarCheck, Gift, MessageCircle, Instagram, Youtube, Facebook, ChevronLeft, Link2, Magnet } from "lucide-react";
import { LOGO_URL } from "../config";
import { useSite, waLink, resolveMedia } from "../siteContext";
import { useLang } from "../i18n";
import { PALETTES } from "../components/admin/AppearanceTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export const LINK_ICONS = { sparkles: Sparkles, book: BookOpen, calendar: CalendarCheck, gift: Gift, whatsapp: MessageCircle, magnet: Magnet, link: Link2 };

export const DEFAULT_LINK_ITEMS = [
  { id: "sunset", ar: "كلمة حلوة و كلمتين", en: "A Kind Word or Two", url: "/p/sunset", image: "", icon: "sparkles" },
  { id: "magnetic", ar: "برنامج Magnetic — المعسكر", en: "The Magnetic Program", url: "/p/magnetic", image: "", icon: "magnet" },
  { id: "courses", ar: "الدورات والبرامج", en: "Courses & Programs", url: "/#courses", image: "", icon: "book" },
  { id: "consult", ar: "الاستشارات الخاصة أونلاين", en: "Private Online Consultations", url: "/#consultations", image: "", icon: "calendar" },
  { id: "gift", ar: "هديتك المجانية — دليل الصباح", en: "Your Free Gift — Morning Guide", url: "/p/morning-routine", image: "", icon: "gift" },
  { id: "whatsapp", ar: "تواصلي معي واتساب", en: "Chat with me on WhatsApp", url: "__whatsapp__", image: "", icon: "whatsapp" },
];

const item = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
});

export default function LinksPage() {
  const settings = useSite();
  const { lang } = useLang();
  const ar = lang === "ar";
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/courses`)
      .then((res) => setCourses((Array.isArray(res.data) ? res.data : []).filter((c) => !c.hidden).slice(0, 3)))
      .catch(() => {});
  }, []);

  const pal = PALETTES.find((p) => p.id === settings.links_palette);
  const C = pal ? pal.colors : null;
  const items = Array.isArray(settings.links_items) && settings.links_items.length ? settings.links_items : DEFAULT_LINK_ITEMS;

  const resolveHref = (l) => (l.url === "__whatsapp__" ? waLink(settings, undefined, lang) : l.url);
  const isExternal = (l) => l.url === "__whatsapp__" || /^https?:\/\//.test(l.url);

  const cardStyle = C ? { backgroundColor: C.surface, borderColor: `${C.accent}40`, color: C.text } : undefined;
  const accentStyle = C ? { color: C.accent } : undefined;
  const mutedStyle = C ? { color: C.muted } : undefined;

  const itemBody = (l) => {
    const Icon = LINK_ICONS[l.icon] || Link2;
    return (
      <>
        <span className="flex items-center gap-3 min-w-0">
          {l.image ? (
            <img src={resolveMedia(l.image)} alt="" className="w-11 h-11 rounded-xl object-cover flex-shrink-0 border border-white/10" />
          ) : (
            <Icon className="w-5 h-5 text-luxury-gold flex-shrink-0" style={accentStyle} strokeWidth={1.5} />
          )}
          <span className="font-semibold text-sm md:text-base truncate">{ar ? l.ar : l.en || l.ar}</span>
        </span>
        <ChevronLeft className="w-4 h-4 text-luxury-gray group-hover:text-luxury-gold transition-colors flex-shrink-0" style={mutedStyle} />
      </>
    );
  };

  const cardClass =
    "flex items-center justify-between w-full border border-luxury-border bg-luxury-surface/60 backdrop-blur rounded-2xl px-5 py-3.5 text-luxury-bone hover:border-luxury-gold hover:text-luxury-gold transition-colors group";

  return (
    <div className="min-h-screen bg-luxury-black hero-vignette relative overflow-hidden" style={C ? { backgroundColor: C.bg } : undefined} data-testid="links-page">
      {settings.links_bg && (
        <>
          <img src={resolveMedia(settings.links_bg)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ backgroundColor: C ? `${C.bg}E6` : "rgba(6,6,7,0.88)" }} />
        </>
      )}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-luxury-gold/10 blur-[120px] pointer-events-none" />
      <div className="relative max-w-md mx-auto px-6 py-14 md:py-20 flex flex-col items-center text-center">
        <motion.div {...item(0)} className="mb-8">
          <img src={LOGO_URL} alt="TheMaya" className="h-12 mx-auto mb-5" />
          <h1 className="font-serif text-3xl text-luxury-bone" style={C ? { color: C.text } : undefined}>TheMaya</h1>
          <p className="text-luxury-gold text-sm mt-2 tracking-wide" style={accentStyle}>{ar ? "مساحة آمنة للتشافي والقيادة الواعية" : "A safe space for healing & conscious leadership"}</p>
          <p className="text-luxury-gray text-xs mt-1" style={mutedStyle} dir="ltr">@themayataher</p>
        </motion.div>

        <div className="w-full space-y-4 mb-10">
          {items.map((l, i) => (
            <motion.div key={l.id || i} {...item(0.1 + i * 0.08)} className="w-full">
              {isExternal(l) ? (
                <a data-testid={`links-item-${i}`} href={resolveHref(l)} target="_blank" rel="noopener noreferrer" className={cardClass} style={cardStyle}>
                  {itemBody(l)}
                </a>
              ) : (
                <Link data-testid={`links-item-${i}`} to={resolveHref(l)} className={cardClass} style={cardStyle}>
                  {itemBody(l)}
                </Link>
              )}
            </motion.div>
          ))}
        </div>

        {courses.length > 0 && (
          <motion.div {...item(0.5)} className="w-full mb-10">
            <p className="flex items-center justify-center gap-2 text-luxury-gold text-xs tracking-[0.2em] mb-4" style={accentStyle}>
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              {ar ? "الأكثر اختياراً" : "MOST CHOSEN"}
            </p>
            <div className="space-y-3">
              {courses.map((c, i) => (
                <Link key={c.id} data-testid={`links-course-${i}`} to={`/course/${c.id}`}
                  className="flex items-center gap-4 w-full border border-luxury-border rounded-2xl p-3 hover:border-luxury-gold transition-colors bg-luxury-surface/40"
                  style={cardStyle}>
                  {c.image && <img src={resolveMedia(c.image)} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />}
                  <div className="text-start flex-1 min-w-0">
                    <p className="text-luxury-bone text-sm font-semibold truncate" style={C ? { color: C.text } : undefined}>{lang === "en" && c.title_en ? c.title_en : c.title}</p>
                    {c.price > 0 && <p className="text-luxury-gold text-xs mt-0.5" style={accentStyle} dir="ltr">{c.price} {String(c.currency || "usd").toUpperCase()}</p>}
                  </div>
                  <ChevronLeft className="w-4 h-4 text-luxury-gray flex-shrink-0" style={mutedStyle} />
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        <motion.div {...item(0.65)} className="flex items-center gap-5 mb-8">
          {settings.instagram && (
            <a data-testid="links-instagram" href={settings.instagram} target="_blank" rel="noopener noreferrer" className="text-luxury-gray hover:text-luxury-gold transition-colors" style={mutedStyle}>
              <Instagram className="w-6 h-6" strokeWidth={1.5} />
            </a>
          )}
          {settings.youtube && (
            <a data-testid="links-youtube" href={settings.youtube} target="_blank" rel="noopener noreferrer" className="text-luxury-gray hover:text-luxury-gold transition-colors" style={mutedStyle}>
              <Youtube className="w-6 h-6" strokeWidth={1.5} />
            </a>
          )}
          {settings.facebook && (
            <a data-testid="links-facebook" href={settings.facebook} target="_blank" rel="noopener noreferrer" className="text-luxury-gray hover:text-luxury-gold transition-colors" style={mutedStyle}>
              <Facebook className="w-6 h-6" strokeWidth={1.5} />
            </a>
          )}
          {settings.tiktok && (
            <a data-testid="links-tiktok" href={settings.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok — TheMaya" title="TikTok — TheMaya" className="text-luxury-gray hover:text-luxury-gold transition-colors" style={mutedStyle}>
              <TikTokIcon className="w-6 h-6" />
            </a>
          )}
        </motion.div>

        <p className="text-luxury-gray text-xs tracking-[0.3em]" style={mutedStyle} dir="ltr">THEMAYA.ONLINE</p>
      </div>
    </div>
  );
}
